import type JSZip from "jszip";
import { tileRange, type Bbox } from "./tiles";
import { NSW_TILE_TEMPLATES } from "../map/basemaps";

/** Tile URL templates per basemap id — the NSW Spatial Services layers the
 *  viewer draws when a flight plays from the offline cache (see
 *  web/src/map/basemaps.ts). Both are ArcGIS caches: {z}/{y}/{x}, row before
 *  column. Creative Commons Attribution, so caching them is allowed; the
 *  Esri tiles drawn online are not cacheable under Esri's licence. */
export const BASEMAPS: Record<string, string> = NSW_TILE_TEMPLATES;

export interface HarvestTilesInput {
  bbox: Bbox;
  minZoom: number;
  maxZoom: number;
  basemaps: string[];
  onProgress?: (done: number, total: number) => void;
}

export interface HarvestTilesResult { fetched: number; failed: number }

const CONCURRENCY = 6;

/** Fetch every basemap tile covering `bbox` into `zip` under `tiles/`. */
export async function harvestTiles(
  zip: JSZip, input: HarvestTilesInput,
): Promise<HarvestTilesResult> {
  const jobs: Array<{ basemap: string; z: number; x: number; y: number }> = [];
  for (const basemap of input.basemaps) {
    for (const t of tileRange(input.bbox, input.minZoom, input.maxZoom)) {
      jobs.push({ basemap, ...t });
    }
  }
  let fetched = 0;
  let failed = 0;
  let done = 0;
  let cursor = 0;

  async function worker(): Promise<void> {
    while (cursor < jobs.length) {
      const job = jobs[cursor];
      cursor += 1;
      if (job === undefined) break;
      const template = BASEMAPS[job.basemap];
      if (template === undefined) { failed += 1; done += 1; continue; }
      const url = template
        .replace("{z}", String(job.z))
        .replace("{x}", String(job.x))
        .replace("{y}", String(job.y));
      try {
        const res = await fetch(url);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        zip.file(
          `tiles/${job.basemap}/${job.z}/${job.x}/${job.y}.png`,
          await res.blob(),
          { createFolders: false },
        );
        fetched += 1;
      } catch {
        failed += 1;
      }
      done += 1;
      input.onProgress?.(done, jobs.length);
    }
  }

  await Promise.all(Array.from({ length: CONCURRENCY }, worker));
  return { fetched, failed };
}
